import "./App-sideBar.css";
import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { confirmLogout } from "../../utils/confirmLogout";
import toast from "react-hot-toast";
import { useNotifications } from "../../context/NotificationsContext";

// Thin-outline icon set matching the seller app's sidebar (layout/src/
// components/usersNavBar/UsersNavBar.jsx) — same packs, same icons where a
// direct counterpart exists, so the two apps read as one design system
// instead of admin's old Font Awesome solid glyphs sitting next to the
// seller's stroke icons.
import { LuLayoutDashboard } from "react-icons/lu";
import { FiShoppingBag, FiSettings, FiLogOut, FiLink, FiSend } from "react-icons/fi";
import { BsBoxSeam } from "react-icons/bs";
import { HiOutlineClipboardList } from "react-icons/hi";
import { GoPeople } from "react-icons/go";
import { TfiWallet } from "react-icons/tfi";
import { clearSession } from "../../utils/session";

const NavBadge = ({ count }) => (count > 0 ? <span className="nav-badge">{count > 99 ? "99+" : count}</span> : null);
// Sits inline on a specific child link (e.g. "Sourcing Requests") rather than
// only the group header — a group's badge is the sum of its children's, but
// only the child badge says which one actually has unread activity.
const ChildNavBadge = ({ count }) => (count > 0 ? <span className="child-nav-badge">{count > 99 ? "99+" : count}</span> : null);

// Which group owns each route. Drives both the parent's active styling and
// which accordion sits open at rest.
const GROUP_PATHS = {
  sourcing: ["/customerRequests", "/create-sourcing-request", "/restock-requests", "/inventory-cancellation-requests"],
  orders: ["/customerOrders", "/create-order", "/order-cancellation-requests", "/cancelled-orders"],
  inventory: ["/customerProducts", "/inventory-deletion-requests", "/deleted-products"],
  customers: ["/customerList", "/createNewCustomer"],
  finance: ["/history", "/topup-requests", "/customerWalletTopUp", "/global-ledger"],
  integrations: ["/integrations", "/integrationJobs", "/integrationSyncLog", "/integrationHeldOrders"],
  manage: ["/notifications", "/sellers", "/subscriptionRequests", "/allSellers"],
};

function AppSideBar({ isMobileOpen, setIsMobileOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const { badgeCount, unreadByEntityType } = useNotifications();

  const ordersUnread = unreadByEntityType.order || 0;
  const sourcingRequestUnread = unreadByEntityType.sourcing_request || 0;
  const restockRequestUnread = unreadByEntityType.restock_request || 0;
  const sourcingUnread = sourcingRequestUnread + restockRequestUnread;
  const inventoryUnread = unreadByEntityType.product || 0;
  const customersUnread = (unreadByEntityType.client || 0) + (unreadByEntityType.account || 0);
  const topUpUnread = unreadByEntityType.top_up_request || 0;
  const walletUnread = (unreadByEntityType.wallet || 0) + (unreadByEntityType.credits || 0);
  const financeUnread = walletUnread + topUpUnread;
  const superAdminUnread = unreadByEntityType.subscription || 0;
  const manageUnread = badgeCount + superAdminUnread;
  // Store integrations: each child counts the notifications its page clears.
  const storesUnread = unreadByEntityType.sales_channel || 0;
  const syncTasksUnread = unreadByEntityType.channel_job || 0;
  const syncActivityUnread = (unreadByEntityType.channel_config || 0) + (unreadByEntityType.order_tracking || 0)
    + (unreadByEntityType.product_link || 0) + (unreadByEntityType.listing_draft || 0);
  const integrationsUnread = storesUnread + syncTasksUnread + syncActivityUnread;

  const activeGroup = Object.keys(GROUP_PATHS).find((key) =>
    GROUP_PATHS[key].some((path) => location.pathname.includes(path))
  ) || null;

  // Which accordion is currently expanded. Click-driven only: the route
  // deliberately does NOT re-open its own group, so picking a page and
  // moving away leaves the sidebar closed rather than pinned open on the
  // section you just left. The parent still carries .active from
  // activeGroup, so the current section stays identifiable while collapsed.
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    let storedUser = localStorage.getItem("user");
    if (!storedUser) {
      clearSession();
      toast.error("Access denied.");
      navigate("/");
      return;
    }
    try { storedUser = JSON.parse(storedUser); }
    catch (error) { clearSession(); navigate("/"); return; }
    if (storedUser.full_name) { setUser(storedUser); }
    else { clearSession(); navigate("/"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Click-only toggle, same on desktop and mobile — nothing opens on hover.
  const toggleGroup = (key) => {
    setOpenMenu((prev) => (prev === key ? null : key));
  };

  // Collapse whatever is open once the pointer leaves the rail. This is a
  // CLOSE handler only — it does not reintroduce open-on-hover. Leaving the
  // group open while the pointer is still inside means you can pick a
  // second link without reopening it.
  //
  // Desktop-guarded: below 1000px the sidebar is a tap-driven full-screen
  // drawer where a stray mouseleave (or a touch that synthesises one) would
  // snap the group shut mid-tap.
  const handleRailLeave = () => {
    // Pinned, the pointer leaves the rail constantly while the menu is meant to
    // be staying put -- closing the group here would fight the whole point.
    if (document.documentElement.dataset.sidebar === "pinned") return;
    if (window.innerWidth > 1000) setOpenMenu(null);
  };

  /* The pin/collapse preference used to live here, driving a footer button.
     It moved to the header (AppHeader.js). The guard in handleRailLeave above
     still works: it reads the document-root attribute, not React state. */

  const closeMobileMenu = () => setIsMobileOpen(false);

  const logout = () => {
    closeMobileMenu();
    confirmLogout(navigate);
  };

  // `Icon` is a react-icons component, not a class string — every icon in
  // this sidebar (and the seller app's) sits inside <span class="icon-wrapper">
  // and sizes itself off that wrapper's font-size, so swapping <i> for
  // <Icon/> needs no other markup change.
  const navGroup = (key, Icon, label, badge, children) => (
    <li className={`nav-item has-children ${openMenu === key ? "open" : ""}`}>
      <button
        type="button"
        className={`nav-link parent-btn ${activeGroup === key ? "active" : ""}`}
        onClick={() => toggleGroup(key)}
        aria-expanded={openMenu === key}
        title={label}
      >
        <span className="icon-wrapper"><Icon /></span>
        <NavBadge count={badge} />
        <span className="link-text">{label}</span>
        {/* Chevron stays Font Awesome — the seller sidebar uses the same
            fa-solid fa-angle-right here, it's not part of the icon swap. */}
        <i className="fas fa-angle-right chevron-icon"></i>
      </button>
      <ul className="child-menu">{children}</ul>
    </li>
  );

  const childLink = (to, label, badge) => (
    <li key={to}>
      <NavLink to={to} className="child-link" onClick={closeMobileMenu}>
        <span>{label}</span>
        {badge !== undefined && <ChildNavBadge count={badge} />}
      </NavLink>
    </li>
  );

  return (
    <nav
      className={`main-nav-bar ${isMobileOpen ? "mobile-active" : ""}`}
      onMouseLeave={handleRailLeave}
    >
      <div className="sidebar-brand">
        <NavLink to="/welcomeAdmin" onClick={closeMobileMenu}>
          <img src="/images/sourship-ICON-LOGO.png" alt="Sourship" />
          <span className="brand-text">SOURSHIP</span>
        </NavLink>
      </div>

      <div className="sidebar-scroll-area">
        <div className="sidebar-section-title"><span>MENU</span></div>
        <ul className="sidebar-nav-list">
          <li className="nav-item">
            <NavLink to="/welcomeAdmin" className="nav-link" onClick={closeMobileMenu} title="Dashboard">
              <span className="icon-wrapper"><LuLayoutDashboard /></span>
              <span className="link-text">Dashboard</span>
            </NavLink>
          </li>

          {navGroup("sourcing", HiOutlineClipboardList, "Sourcing", sourcingUnread, [
            childLink("/customerRequests", "Sourcing Requests", sourcingRequestUnread),
            childLink("/create-sourcing-request", "Create Request"),
            childLink("/restock-requests", "Restock Requests", restockRequestUnread),
            childLink("/inventory-cancellation-requests", "Requests Cancellation"),
          ])}

          <li className="nav-item">
            <NavLink to="/send-to-zq" className="nav-link" onClick={closeMobileMenu} title="Send to ZQ">
              <span className="icon-wrapper"><FiSend /></span>
              <span className="link-text">Send to ZQ</span>
            </NavLink>
          </li>

          {navGroup("orders", FiShoppingBag, "Orders", ordersUnread, [
            childLink("/customerOrders", "Orders Management", ordersUnread),
            childLink("/create-order", "Create Order"),
            childLink("/order-cancellation-requests", "Orders Cancellation"),
            childLink("/cancelled-orders", "Cancelled Orders"),
          ])}

          {navGroup("inventory", BsBoxSeam, "Inventory", inventoryUnread, [
            childLink("/customerProducts", "Inventory List", inventoryUnread),
            childLink("/inventory-deletion-requests", "Deletion Requests"),
            childLink("/deleted-products", "Deleted Products"),
          ])}

          {navGroup("customers", GoPeople, "End-Customers", customersUnread, [
            childLink("/customerList", "End-Customers List", customersUnread),
            childLink("/createNewCustomer", "Add New End-Customer"),
          ])}

          {navGroup("finance", TfiWallet, "Finance & Wallet", financeUnread, [
            childLink("/global-ledger", "Transactions History", walletUnread),
            childLink("/customerWalletTopUp", "Manual Adjustment"),
            childLink("/topup-requests", "Top-Up Requests", topUpUnread),
            childLink("/history", "Activity History"),
          ])}

          {/* Store integrations. Operational visibility only — connecting a store is
              the seller doing it in their own app; this is where an admin sees what
              is connected and what is failing. */}
          {navGroup("integrations", FiLink, "Sales Channels", integrationsUnread, [
            childLink("/integrations", "Connected Stores", storesUnread),
            childLink("/integrationHeldOrders", "Held Orders"),
            childLink("/integrationJobs", "Sync Tasks", syncTasksUnread),
            childLink("/integrationSyncLog", "Sync Activity", syncActivityUnread),
          ])}

          {navGroup("manage", FiSettings, "Manage", manageUnread, [
            childLink("/notifications", "Notifications", badgeCount),
            // Every admin, scoped to their own assigned sellers
            childLink("/sellers", "My Sellers"),
            // Hidden from every other admin. The real gate is server-side
            // (require_super_admin.php on every subscription_requests /
            // sellers-scope=all endpoint); this only hides the links.
            ...(user?.is_super_admin
              ? [
                  childLink("/subscriptionRequests", "Subscription Requests", superAdminUnread),
                  childLink("/allSellers", "All Sellers"),
                ]
              : []),
          ])}
        </ul>
      </div>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          {/* The admin's own initials, as in the header. It was the fixed text
              "AD", so the two avatars on the screen disagreed. */}
          <div className="sidebar-avatar">
            {(user?.full_name || "Admin User").split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase()}
          </div>
          <div className="sidebar-user-details">
            <h4>{user ? user.full_name : "Admin User"}</h4>
            <p>{user?.email || "admin@sourship.com"}</p>
          </div>
        </div>
        <button type="button" className="logout-btn" onClick={logout} title="Log out">
          <span className="icon-wrapper"><FiLogOut /></span>
          <span className="link-text">Log Out</span>
        </button>
      </div>
    </nav>
  );
}

export default AppSideBar;
